'use client';

import { Box } from '@mui/material';
import { Vertical } from '@repo/supergraph/marketplace/constants';
import { type StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type { CrossSellSection, Roundup } from '@repo/supergraph/roundup/base/types';
import { MIN_CROSS_SELL_PRODUCT_CARDS } from '@repo/supergraph/roundup/constants';
import { useGeo } from '../../../GeoTargeting/GeoProvider';
import { ProductCardCategorySectionsWrapper } from '../../../SummaryTableProductCardsWrapper/ProductCardCategorySectionsWrapper';
import { SummaryTableSectionWrapper } from '../../../SummaryTableProductCardsWrapper/SummaryTableSectionWrapper';
import { AdditionalRecommendations } from '../../components/AdditionalRecommendations';
import { CrossSellModule } from '../../components/CrossSellModule/CrossSellModule';
import { getFilterSlugForPage } from '../../components/OffersFilter/utils';
import ProductCardCategorySections from '../../components/ProductCardCategorySections';
import { ProductsFiltersWrapper } from '../../components/ProductsSection/ProductsFiltersWrapper';
import { SummaryTableSection } from '../../components/SummaryTable/SummaryTableSection';

const crossSellOuterSx = {
  maxWidth: { xs: '100%', md: '82%' },
  margin: 'auto',
  marginTop: 5,
  marginBottom: 8,
  paddingBottom: 2,
} as const;

export type ProductsSectionProps = {
  rows: Roundup['summaryTableSection']['rows'];
  sectionTitle: string;
  isShowingAdditionalRecommendations: boolean;
  productCardsSection: {
    categorySections: Roundup['productCardsSection']['categorySections'];
  };
  additionalRecommendations: Roundup['additionalRecommendations'];
  crossSellSection?: CrossSellSection;
  showSummaryTable: boolean;
  slug: string;
  initialLocation?: StateAbbreviation;
  pageUrl: string;
  vertical: Vertical;
  shouldApplyStylingUpdates?: boolean;
  suppressApyTermPrefix?: boolean;
  isImproveQualityTrafficExperimentActive?: boolean;
  shouldDisplayWealthFrontModal?: boolean;
};

export function ProductsSection({
  additionalRecommendations,
  rows,
  crossSellSection,
  sectionTitle,
  isShowingAdditionalRecommendations,
  productCardsSection,
  showSummaryTable,
  slug,
  initialLocation,
  pageUrl,
  vertical,
  shouldApplyStylingUpdates = false,
  suppressApyTermPrefix = false,
  isImproveQualityTrafficExperimentActive = false,
  shouldDisplayWealthFrontModal = false,
}: ProductsSectionProps) {
  const { regionCode } = useGeo();
  const showCrossSellSection =
    crossSellSection &&
    crossSellSection.productCards &&
    crossSellSection.productCards.length >= MIN_CROSS_SELL_PRODUCT_CARDS;
  const filterSlug = getFilterSlugForPage(slug, true, Boolean(initialLocation));
  const isFilterEnabled = Boolean(filterSlug);

  const summaryTableSharedProps = {
    sectionTitle,
    regionCode,
    slug,
    showSkeleton: false,
    pageUrl,
    shouldApplyStylingUpdates,
    suppressApyTermPrefix,
    isImproveQualityTrafficExperimentActive,
    shouldDisplayWealthFrontModal,
  };

  const renderSummaryTable = () => {
    if (!showSummaryTable) {
      return null;
    }
    if (isFilterEnabled) {
      return <SummaryTableSectionWrapper {...summaryTableSharedProps} />;
    }
    return <SummaryTableSection {...summaryTableSharedProps} rows={rows} />;
  };

  const renderProductCards = () => {
    const cardProps = {
      categorySections: productCardsSection.categorySections,
      isSeoBankingFFActive: false,
      regionCode,
      showSkeleton: false,
      slug,
      shouldApplyStylingUpdates,
      isImproveQualityTrafficExperimentActive,
      shouldDisplayWealthFrontModal,
    };
    if (isFilterEnabled) {
      return <ProductCardCategorySectionsWrapper {...cardProps} />;
    }
    return <ProductCardCategorySections {...cardProps} />;
  };

  return (
    <Box
      component="section"
      aria-label={sectionTitle}
      sx={{ marginTop: { xs: isFilterEnabled ? 4 : 0, md: 0 } }}
    >
      <ProductsFiltersWrapper
        sortedRows={rows}
        slug={slug}
        initialLocation={initialLocation}
        vertical={vertical}
        isPaid
      >
        {renderSummaryTable()}
        {showCrossSellSection && (
          <Box sx={crossSellOuterSx}>
            <CrossSellModule crossSellSection={crossSellSection} regionCode={regionCode} />
          </Box>
        )}
        {renderProductCards()}
        {isShowingAdditionalRecommendations && additionalRecommendations && (
          <AdditionalRecommendations
            additionalRecommendations={additionalRecommendations}
            vertical={vertical}
          />
        )}
      </ProductsFiltersWrapper>
    </Box>
  );
}
